"use client";

import Button from "@/components/flowbite/button/button";
import Logo from "@/components/flowbite/logo/logo";
import Typography from "@/components/flowbite/typography/typography";
import { PinAltIcon } from "@/components/icons-lib";

import { AddressSearchInput } from "./address-search-input";

import type { AddressSearchInputProps } from "./address-search-input";

export interface FindMySchoolHeaderProps extends Pick<
  AddressSearchInputProps,
  | "value"
  | "onChange"
  | "suggestions"
  | "onSearch"
  | "onSelect"
  | "onClear"
  | "isLoading"
  | "showNoResults"
  | "placeholder"
> {
  districtName: string;
  onDropPinClick: () => void;
  dropPinMode?: boolean;
  mobile?: boolean;
}

export function FindMySchoolHeader({
  districtName,
  value,
  onChange,
  suggestions,
  onSearch,
  onSelect,
  onClear,
  isLoading,
  showNoResults,
  placeholder,
  onDropPinClick,
  dropPinMode = false,
  mobile = false,
}: FindMySchoolHeaderProps) {
  if (mobile) {
    return (
      <header className="z-50 flex min-h-14 w-full shrink-0 items-center justify-between gap-3 border-b border-[#EDECEB] bg-white px-3 py-2">
        <h1 className="line-clamp-2 min-w-0 text-[13px]/[16px] font-semibold tracking-[-0.01em] text-[#161515]">
          {districtName}
        </h1>
        <div className="flex shrink-0 flex-col items-end gap-0.5">
          <span className="text-[8px]/[10px] font-medium uppercase tracking-[0.08em] text-[#8A8586]">
            Powered by
          </span>
          <div className="[&>svg]:h-4 [&>svg]:w-auto">
            <Logo />
          </div>
        </div>
      </header>
    );
  }

  return (
    <header className="relative z-40 w-full bg-background-primary px-4 py-3">
      <div className="flex flex-col gap-4">
        <div className="flex items-center">
          <Logo />
        </div>

        <div className="flex flex-col gap-1">
          <Typography.H4
            capitalize={false}
            colorShade={500}
          >
            {districtName}
          </Typography.H4>
          <Typography.BodyMRegular
            capitalize={false}
            colorShade={400}
          >
            Enter your home address to discover your assigned Elementary,
            Middle, or High School.
          </Typography.BodyMRegular>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="min-w-[200px] flex-1">
            <AddressSearchInput
              isLoading={isLoading}
              placeholder={placeholder}
              showNoResults={showNoResults}
              suggestions={suggestions}
              value={value}
              onChange={onChange}
              onClear={onClear}
              onSearch={onSearch}
              onSelect={onSelect}
            />
          </div>
          <Typography.BodyMRegular
            capitalize={false}
            className="shrink-0"
            colorShade={400}
          >
            or
          </Typography.BodyMRegular>
          <Button
            className="shrink-0"
            disabled={dropPinMode}
            size="small"
            typography="bodyMMedium"
            variant="primary"
            onClick={onDropPinClick}
          >
            <PinAltIcon className="h-5 w-5" />
            Drop pin on map
          </Button>
        </div>
      </div>
    </header>
  );
}
